import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import { BLOG_POSTS } from '../data/blogPosts';
import EmailCapture from '../components/EmailCapture';

const CATEGORY_COLORS = {
  'Investing':  '#a78bfa',
  'Crypto':     '#f59e0b',
  'Markets':    '#22c55e',
  'Planning':   '#e879f9',
  'Retirement': '#34d399',
  'Economy':    '#fb923c',
  'Savings':    '#38bdf8',
  'Budgeting':  '#818cf8',
  'Debt':       '#ef4444',
  'Credit':     '#f472b6',
  'Taxes':      '#facc15',
  'Income':     '#4ade80',
  'Insurance':  '#94a3b8',
  'Real Estate':'#f97316',
  'Basics':     '#67e8f9',
  'Mindset':    '#c084fc',
};

// Deduplicate at module level for category list (order doesn't matter here)
const _SEEN = new Set();
const _UNIQUE = BLOG_POSTS.filter(Boolean).filter(p => { if (_SEEN.has(p.slug)) return false; _SEEN.add(p.slug); return true; });
const ALL_CATEGORIES = ['All', ...Array.from(new Set(_UNIQUE.map(p => p.category).filter(Boolean)))];

const DATA_TOOLS = [
  {
    emoji: '📊',
    title: 'Savings by Age',
    description: 'How much should you have saved? See benchmarks for every life stage — teens through retirement.',
    color: '#38bdf8',
    to: '/savings-by-age',
    label: 'Benchmarks',
  },
  {
    emoji: '💰',
    title: 'Contribution Limits',
    description: 'IRS contribution limits for Roth IRA, 401k, HSA, and more — by account type and year.',
    color: '#34d399',
    to: '/contribution-limits/roth-ira/2026',
    label: 'IRS Data',
  },
  {
    emoji: '📈',
    title: 'Market Returns by Year',
    description: 'Annual returns for S&P 500, NASDAQ, all 11 sectors, bonds, gold, and Bitcoin — 2015 through 2025.',
    color: '#a78bfa',
    to: '/market-returns',
    label: 'Markets',
  },
  {
    emoji: '🗺️',
    title: 'Savings by State',
    description: 'Compare average retirement savings, savings rates, and cost of living across all 50 states.',
    color: '#fb923c',
    to: '/savings-by-state',
    label: 'By State',
  },
];

export default function Blog() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') === 'data' ? 'data' : 'guides';
  });
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'data') setActiveTab('data');
  }, [location.search]);

  // Deduplicate by slug — keep first occurrence (preserves category assignment)
  const UNIQUE_POSTS = useMemo(() => {
    const seen = new Set();
    return BLOG_POSTS.filter(Boolean).filter(p => {
      if (seen.has(p.slug)) return false;
      seen.add(p.slug);
      return true;
    });
  }, []);

  const filtered = useMemo(() => {
    let posts = UNIQUE_POSTS;
    if (activeCategory !== 'All') posts = posts.filter(p => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      posts = posts.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    }
    return posts;
  }, [search, activeCategory, UNIQUE_POSTS]);

  // Group posts by category for the "All" view (no search, no specific category selected)
  const grouped = useMemo(() => {
    if (activeCategory !== 'All' || search.trim()) return null;
    const map = {};
    UNIQUE_POSTS.forEach(p => {
      if (!map[p.category]) map[p.category] = [];
      map[p.category].push(p);
    });
    // Sort sections by category name
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [search, activeCategory, UNIQUE_POSTS]);

  return (
    <div style={s.page}>
      <SEO
        title="Personal Finance Guides — KnowFi"
        description="258 free personal finance guides covering investing, crypto, markets, budgeting, debt, retirement, taxes, and the economy. Plain English. No jargon."
        path="/blog"
        keywords="personal finance guides, investing, budgeting, debt, retirement, crypto, economy, financial literacy"
      />
      <div style={s.glow} />
      <div style={s.inner}>

        {/* Header */}
        <div style={s.header}>
          <div style={s.eyebrow}>📚 Free Resources</div>
          <h1 style={s.title}>Money Explained Simply</h1>
          <p style={s.sub}>Guides, data, and tools — no jargon, no Wall Street nonsense.</p>
        </div>

        {/* Tabs */}
        <div style={s.tabs}>
          <button
            style={{ ...s.tab, ...(activeTab === 'guides' ? s.tabActive : {}) }}
            onClick={() => setActiveTab('guides')}
          >📖 Guides</button>
          <button
            style={{ ...s.tab, ...(activeTab === 'data' ? s.tabActiveData : {}) }}
            onClick={() => setActiveTab('data')}
          >📊 Data & Tools</button>
        </div>

        {/* GUIDES TAB */}
        {activeTab === 'guides' && (
          <>
            {/* Search */}
            <div style={s.searchWrap}>
              <span style={s.searchIcon}>🔍</span>
              <input
                style={s.search}
                type="text"
                placeholder="Search guides, e.g. 'emergency fund' or 'Roth IRA'..."
                value={search}
                onChange={e => { setSearch(e.target.value); setActiveCategory('All'); }}
              />
              {search && <button style={s.clearBtn} onClick={() => setSearch('')}>✕</button>}
            </div>

            {/* Category filter */}
            {!search && (
              <div style={s.catNav}>
                {ALL_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    style={{
                      ...s.catBtn,
                      ...(activeCategory === cat ? {
                        background: cat === 'All' ? 'rgba(56,189,248,0.12)' : (CATEGORY_COLORS[cat] || '#64748b') + '18',
                        border: `1px solid ${cat === 'All' ? '#38bdf8' : (CATEGORY_COLORS[cat] || '#64748b')}44`,
                        color: cat === 'All' ? '#38bdf8' : (CATEGORY_COLORS[cat] || '#64748b'),
                      } : {}),
                    }}
                    onClick={() => { setActiveCategory(cat); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  >{cat}</button>
                ))}
              </div>
            )}

            {/* Stats */}
            <div style={s.statsBar}>
              <span style={s.statText}>
                {activeCategory === 'All' && !search
                  ? `${UNIQUE_POSTS.length} guides across ${ALL_CATEGORIES.length - 1} categories`
                  : `${filtered.length} ${filtered.length === 1 ? 'guide' : 'guides'}${activeCategory !== 'All' && !search ? ` in ${activeCategory}` : ''}`}
              </span>
              {activeCategory !== 'All' && !search && (
                <button style={s.resetBtn} onClick={() => setActiveCategory('All')}>Show all →</button>
              )}
            </div>

            {filtered.length === 0 ? (
              <div style={s.empty}>
                <div style={s.emptyIcon}>🔍</div>
                <div style={s.emptyText}>No guides match "{search}"</div>
                <button style={s.resetBtn} onClick={() => setSearch('')}>Clear search</button>
              </div>
            ) : grouped ? (
              // "All" view — grouped by category, each section shows only its own guides
              <div>
                {grouped.map(([category, posts]) => (
                  <div key={category} style={s.categorySection}>
                    <div style={s.sectionHeader}>
                      <span style={{
                        ...s.sectionDot,
                        background: CATEGORY_COLORS[category] || '#64748b',
                      }} />
                      <h2 style={{ ...s.sectionTitle, color: CATEGORY_COLORS[category] || '#94a3b8' }}>{category}</h2>
                      <span style={s.sectionCount}>{posts.length} {posts.length === 1 ? 'guide' : 'guides'}</span>
                      <button
                        style={{ ...s.resetBtn, marginLeft: 'auto' }}
                        onClick={() => { setActiveCategory(category); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      >View all →</button>
                    </div>
                    <div style={s.grid}>
                      {posts.slice(0, 6).map(post => (
                        <Link key={post.slug} to={`/blog/${post.slug}`} style={s.card}>
                          <div style={s.cardTop}>
                            <span style={{
                              ...s.catBadge,
                              background: (CATEGORY_COLORS[post.category] || '#64748b') + '18',
                              color: CATEGORY_COLORS[post.category] || '#64748b',
                              border: `1px solid ${(CATEGORY_COLORS[post.category] || '#64748b')}33`,
                            }}>{post.category}</span>
                            <span style={s.readTime}>{post.readTime} read</span>
                          </div>
                          <div style={s.cardEmoji}>{post.emoji}</div>
                          <h2 style={s.cardTitle}>{post.title}</h2>
                          <p style={s.cardDesc}>{post.description}</p>
                          <div style={s.cardCta}>Read article →</div>
                        </Link>
                      ))}
                    </div>
                    {posts.length > 6 && (
                      <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <button
                          style={s.viewMoreBtn}
                          onClick={() => { setActiveCategory(category); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        >See all {posts.length} {category} guides →</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Filtered view — specific category or search results
              <div style={s.grid}>
                {filtered.map(post => (
                  <Link key={post.slug} to={`/blog/${post.slug}`} style={s.card}>
                    <div style={s.cardTop}>
                      <span style={{
                        ...s.catBadge,
                        background: (CATEGORY_COLORS[post.category] || '#64748b') + '18',
                        color: CATEGORY_COLORS[post.category] || '#64748b',
                        border: `1px solid ${(CATEGORY_COLORS[post.category] || '#64748b')}33`,
                      }}>{post.category}</span>
                      <span style={s.readTime}>{post.readTime} read</span>
                    </div>
                    <div style={s.cardEmoji}>{post.emoji}</div>
                    <h2 style={s.cardTitle}>{post.title}</h2>
                    <p style={s.cardDesc}>{post.description}</p>
                    <div style={s.cardCta}>Read article →</div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        {/* DATA & TOOLS TAB */}
        {activeTab === 'data' && (
          <>
            <p style={s.dataIntro}>Data-driven pages for every financial question — real numbers, no fluff.</p>
            <div style={s.grid}>
              {DATA_TOOLS.map(item => (
                <Link key={item.to} to={item.to} style={s.card}>
                  <div style={s.cardTop}>
                    <span style={{
                      ...s.catBadge,
                      background: item.color + '18',
                      color: item.color,
                      border: `1px solid ${item.color}33`,
                    }}>{item.label}</span>
                  </div>
                  <div style={s.cardEmoji}>{item.emoji}</div>
                  <h2 style={s.cardTitle}>{item.title}</h2>
                  <p style={s.cardDesc}>{item.description}</p>
                  <div style={{ ...s.cardCta, color: item.color }}>Explore →</div>
                </Link>
              ))}
            </div>
          </>
        )}

      </div>

      {/* Email Capture */}
      <EmailCapture
        variant="inline"
        source="blog-bottom"
        title="Stay sharp"
        subtitle="The KnowFi Brief — market news without the jargon."
      />
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '60px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:  { position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '600px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.08) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 1 },

  header:  { textAlign: 'center', marginBottom: '36px' },
  eyebrow: { display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', padding: '5px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', marginBottom: '16px' },
  title:   { fontSize: 'clamp(32px,5vw,52px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-1px' },
  sub:     { color: '#64748b', fontSize: '16px', margin: 0 },

  tabs:        { display: 'flex', gap: '8px', marginBottom: '32px', borderBottom: '1px solid #1e293b', paddingBottom: '0' },
  tab:         { background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#64748b', padding: '10px 20px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '-1px', borderRadius: '0' },
  tabActive:   { color: '#38bdf8', borderBottom: '2px solid #38bdf8' },
  tabActiveData: { color: '#fb923c', borderBottom: '2px solid #fb923c' },

  searchWrap: { position: 'relative', marginBottom: '20px' },
  searchIcon: { position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', pointerEvents: 'none' },
  search:     { width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', color: '#f8fafc', padding: '14px 44px', fontSize: '15px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },
  clearBtn:   { position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '14px', fontFamily: 'inherit', padding: '4px 8px', borderRadius: '6px' },

  catNav:  { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' },
  catBtn:  { background: '#1e293b', border: '1px solid #334155', color: '#64748b', borderRadius: '20px', padding: '6px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },

  statsBar: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' },
  statText: { color: '#475569', fontSize: '13px' },
  resetBtn: { background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', padding: 0 },

  empty:     { textAlign: 'center', padding: '60px 24px' },
  emptyIcon: { fontSize: '40px', marginBottom: '12px' },
  emptyText: { color: '#475569', fontSize: '16px', marginBottom: '16px' },

  dataIntro: { color: '#64748b', fontSize: '15px', marginBottom: '28px' },

  categorySection: { marginBottom: '52px' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #1e293b' },
  sectionDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  sectionTitle: { fontSize: '20px', fontWeight: '800', margin: 0, letterSpacing: '-0.3px' },
  sectionCount: { fontSize: '12px', color: '#475569', fontWeight: '600' },
  viewMoreBtn: { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' },
  card: { background: '#1e293b', borderRadius: '16px', padding: '24px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '10px', transition: 'border-color 0.2s' },
  cardTop:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  catBadge:  { fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.3px' },
  readTime:  { fontSize: '12px', color: '#475569' },
  cardEmoji: { fontSize: '32px', lineHeight: 1 },
  cardTitle: { fontSize: '17px', fontWeight: '800', color: '#f1f5f9', lineHeight: 1.35, margin: 0, letterSpacing: '-0.3px' },
  cardDesc:  { fontSize: '14px', color: '#64748b', lineHeight: 1.6, margin: 0, flex: 1 },
  cardCta:   { fontSize: '13px', color: '#38bdf8', fontWeight: '600', marginTop: '4px' },
};
